//@ts-nocheck
import React from 'react';
import axios from 'axios'
import { useGetToken } from '../../hooks/useGetToken'
import Loading from '../../components/Loading';
import ScrollableTable from '../../components/ScrollableTable';
import { useGlobalStateContext } from "../../context/globalState";
import { readAuthPath } from "../LoginChooser";

const SUPERVISOR_USER = process.env.REACT_APP_SUPERVISOR_USERS
const SUPER_ADMIN_USER = process.env.REACT_APP_SUPER_ADMIN_USERS
const FL_KEY_USER = process.env.REACT_APP_FL_KEY_USER
const FL_TRAINER = process.env.REACT_APP_FL_TRAINER
const AREA_KEY_USER = process.env.REACT_APP_AREA_USER
const allowedRoles = [SUPERVISOR_USER, SUPER_ADMIN_USER, FL_KEY_USER, FL_TRAINER, AREA_KEY_USER];

interface AdminLandingPageProps {
    onSignOut: () => void;
}

const AdminLandingPage: React.FC<AdminLandingPageProps> = ({ onSignOut }) => {
    const [getToken] = useGetToken();
    const { authState, setAdminRole, setPermissions } = useGlobalStateContext()
    const [isLoading, setIsLoading] = React.useState<boolean>(true);

    React.useEffect(() => {
        fetchUserRoles();
    }, []);


    const fetchUserRoles = async () => {
        let userRole: string | null = null;
        const token = await getToken();
        if (!token) {
            console.error('No token found');
            setAdminRole(null);
            setIsLoading(false);
            return;
        }

        // Subcontractor (Auth0) sessions have no Azure AD groups and cannot call
        // Microsoft Graph. Ask the backend what this caller may do via the lightweight
        // GET /v1/me endpoint (returns the authorizer-resolved role/permissions, no DB).
        // Admin dashboard requires the admin:user:read permission.
        if (readAuthPath() === 'subcontractor') {
            // Use axios with a manual Authorization header - the same pattern the rest of
            // the app uses (the shared Service axios instance is fitter-specific and reads
            // its token from localStorage, which the Auth0 flow never sets). axios throws
            // on non-2xx, so a 403 from the backend lands in catch -> not authorized.
            try {
                const config: AxiosRequestConfig = {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
                    },
                };
                const res = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/v1/me`, config);
                const perms: string[] = res.data?.permissions || [];
                setPermissions(perms);
                const isAdmin = perms.includes('*') || perms.includes('admin:user:read');
                setAdminRole(isAdmin ? 'SUBCON_ADMIN' : null);
            } catch (error: any) {
                console.warn('Subcontractor access check failed', error?.response?.status);
                setPermissions([]);
                setAdminRole(null);
            }
            setIsLoading(false);
            return;
        }

        // What this caller may actually do, asked of the backend rather than
        // inferred from the AD group. The group still decides which dashboard
        // they land on; the permissions decide which actions the screens offer,
        // so granting a group a new capability is a change to
        // ad_group_permissions and needs no release. GET /v1/me carries no
        // permission of its own - it only echoes the authorizer context.
        try {
            const meConfig: AxiosRequestConfig = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
                },
            };
            const me = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/v1/me`, meConfig);
            setPermissions(me.data?.permissions || []);
        } catch (error: any) {
            // Not fatal: the role below still decides access to the dashboard.
            // An empty list simply hides the capability-gated entries, which is
            // the safe direction to fail in.
            console.warn('Could not resolve permissions', error?.response?.status);
            setPermissions([]);
        }

        let userGroups = authState?.user?.idTokenClaims?.groups || [];
        if (userGroups.length === 0) {
            const formData = {
                securityEnabledOnly: false
            }
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token?.startsWith('Bearer ') ? token : `Bearer ${token}`,
                },
            };
            const response = await axios.post(
                `https://graph.microsoft.com/v1.0/users/${authState.user?.localAccountId}/getMemberObjects`,
                formData,
                config
            );
            console.log('User groups fetched:', response.data);
            userGroups = response.data.value || [];
        }

        if (userGroups && userGroups.length > 0) {
            if (userGroups.includes(SUPER_ADMIN_USER)) {
                setAdminRole('SUPER_ADMIN_USER')
            } else if (userGroups.includes(SUPERVISOR_USER)) {
                setAdminRole('SUPERVISOR_USER')
            } else if (userGroups.includes(AREA_KEY_USER)) {
                setAdminRole('AREA_KEY_USER')
            } else if (userGroups.includes(FL_KEY_USER)) {
                setAdminRole('FL_KEY_USER')
            } else if (userGroups.includes(FL_TRAINER)) {
                setAdminRole('FL_TRAINER')
            } else {
                console.warn('User does not have the required role');
                setAdminRole(null);
            }
        } else {
            console.warn('User groups are empty');
            setAdminRole(null);
        }
        setIsLoading(false);
    };

    return (
        <div>
            {isLoading ? (
                <Loading />
            ) : <ScrollableTable onSignOut={onSignOut} />
            }
        </div>
    );
};

export default AdminLandingPage;